<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import { useI18n } from 'vue-i18n'
import { formatBib } from '@shagan/shared-utils'
import { Roles } from '@/constants.ts'
import { formatCategoryLabel, type CategoryLabelParts } from '@/utils/formatCategoryLabel.ts'
import BibTemplateEditor from '@/components/events/BibTemplateEditor.vue'
import BibRangesEditor from '@/components/events/BibRangesEditor.vue'
import EventSelect from '@/components/events/EventSelect.vue'
import bibsHttpService, {
  type BibCategoryPool,
  type PrintMode,
  type PrintSelection,
} from '@/services/http/bibsHttpService.ts'
import { extractApiError } from '@/services/http/apiError.ts'

definePage({
  meta: {
    allowedRoles: [Roles.Admin, Roles.Manager],
  },
})

const { t, te } = useI18n()
const toast = useToast()
const confirm = useConfirm()
const queryClient = useQueryClient()

// Выбор мероприятия — единый EventSelect (дефолт по featured-событию из /api/admin/events).
const eventId = ref<string | null>(null)

// Активная вкладка хранится в URL (?tab=…) — переживает обновление страницы. Значение = ключ
// вкладки (Tabs в PrimeVue 4 выбирает по value). Начальное читаем из route при монтировании, а на
// смену пишем адрес через history.replaceState — БЕЗ навигации роутера: иначе на каждый клик вкладки
// прогоняется beforeEach-гвард (декод JWT, проверка ролей) и лишняя перерисовка, переключение виснет.
const route = useRoute()
const TAB_KEYS = ['main', 'unassigned', 'pool', 'issuanceHistory', 'printHistory'] as const
type TabKey = (typeof TAB_KEYS)[number]
function tabKeyFromQuery(): TabKey {
  const k = String(route.query.tab)
  return (TAB_KEYS as readonly string[]).includes(k) ? (k as TabKey) : 'main'
}
// Ленивый рендер вкладок: панели держатся в DOM, поэтому контент рендерим только у активной
// (v-if="activeTabKey === ...") — иначе 5 таблиц/сотни строк строятся сразу → лаги переключения.
const activeTabKey = ref<TabKey>(tabKeyFromQuery())
watch(activeTabKey, (key) => {
  const url = new URL(window.location.href)
  if (url.searchParams.get('tab') === key) return
  url.searchParams.set('tab', key)
  window.history.replaceState(window.history.state, '', url)
})

const poolQueryKey = computed(() => ['admin', 'bibs', 'pool', eventId.value])

const poolQuery = useQuery({
  queryKey: poolQueryKey,
  queryFn: () => bibsHttpService.getPool(eventId.value!),
  enabled: computed(() => !!eventId.value),
})

const batchesQuery = useQuery({
  queryKey: computed(() => ['admin', 'bibs', 'batches', eventId.value]),
  queryFn: () => bibsHttpService.getPrintBatches(eventId.value!),
  enabled: computed(() => !!eventId.value),
})

const categories = computed(() => poolQuery.data.value?.categories ?? [])
const withoutNumber = computed(() => poolQuery.data.value?.withoutNumber ?? [])
const printBatches = computed(() => batchesQuery.data.value ?? [])

// Количество участников «без номера» по категориям — для колонки в «Основное».
const withoutNumberCountByCategory = computed(() => {
  const m = new Map<string, number>()
  for (const r of withoutNumber.value) m.set(r.categoryId, (m.get(r.categoryId) ?? 0) + 1)
  return m
})

// Подпись категории: групповая хранит имя в БД, индивидуальная композируется из структурных полей.
function catLabel(c: BibCategoryPool): string {
  if (c.categoryName) return c.categoryName
  return formatCategoryLabel(
    {
      categoryDistanceKm: c.distanceKm,
      categoryGenderCode: c.genderCode,
      categoryAgeMin: c.ageMin,
      categoryAgeMax: c.ageMax,
      categoryIsGroupRace: c.isGroupRace,
    },
    t,
  )
}

const categoryLabelById = computed(() => {
  const m = new Map<string, string>()
  for (const c of categories.value) m.set(c.categoryId, catLabel(c))
  return m
})
function labelOf(categoryId: string, fallback?: string | null): string {
  return categoryLabelById.value.get(categoryId) ?? fallback ?? '—'
}

// Минимальный старт диапазона категории — ключ сортировки реестра (#4).
function categoryRangeStart(c: BibCategoryPool): number {
  return c.ranges.length ? Math.min(...c.ranges.map((r) => r.start)) : Number.MAX_SAFE_INTEGER
}
const categoriesSorted = computed(() =>
  [...categories.value].sort((a, b) => categoryRangeStart(a) - categoryRangeStart(b)),
)

const categoryOptions = computed(() =>
  categoriesSorted.value.map((c) => ({ label: catLabel(c), value: c.categoryId })),
)

// Редактор диапазонов номеров (перенесён сюда из «Мероприятий»): пакетно по всем категориям.
const rangesVisible = ref(false)
const rangesEditorCategories = computed(() =>
  categoriesSorted.value.map((c) => ({
    id: c.categoryId,
    label: catLabel(c),
    ranges: c.ranges,
  })),
)
function onRangesSaved() {
  rangesVisible.value = false
  invalidatePool()
}

function invalidatePool() {
  queryClient.invalidateQueries({ queryKey: ['admin', 'bibs', 'pool', eventId.value] })
  queryClient.invalidateQueries({ queryKey: ['admin', 'bibs', 'slots', eventId.value] })
  queryClient.invalidateQueries({ queryKey: ['admin', 'bibs', 'history', eventId.value] })
}
function invalidateBatches() {
  queryClient.invalidateQueries({ queryKey: ['admin', 'bibs', 'batches', eventId.value] })
}

function notify(success: boolean, detail: string) {
  toast.add({
    severity: success ? 'success' : 'error',
    summary: success ? t('common.success') : t('common.error'),
    detail,
    life: 4000,
  })
}

function onApiError(err: unknown) {
  const e = err as { response?: { data?: unknown } }
  const code = extractApiError(e?.response?.data)
  // Локализуем код ошибки; неизвестный код показываем как есть (fallback).
  const key = `bibs.errorCodes.${code}`
  notify(false, te(key) ? t(key) : code)
}

// «Нет номеров для печати» — не ошибка, а штатное уведомление (зелёный тост) для любой выборки.
// Для «не напечатанных» уточняем: всё уже распечатано ранее. Прочие коды — обычная ошибка.
function onPrintError(err: unknown, selection: PrintSelection, close: () => void) {
  const e = err as { response?: { data?: unknown } }
  if (extractApiError(e?.response?.data) === 'no_slots_to_print') {
    notify(
      true,
      selection === 'assigned_unprinted'
        ? t('bibs.toasts.allAlreadyPrinted')
        : t('bibs.errorCodes.no_slots_to_print'),
    )
    close()
    return
  }
  onApiError(err)
}

// Печать именным шрифтом без казахских букв: ФИО/категория выйдут «квадратами» — предупреждаем.
function warnFontsIfAny(families: string[]) {
  if (!families.length) return
  toast.add({
    severity: 'warn',
    summary: t('bibs.toasts.fontWarnTitle'),
    detail: t('bibs.toasts.fontWarn', { fonts: families.join(', ') }),
    life: 8000,
  })
}

// ──────────────── Выдача номеров ────────────────
const assignMutation = useMutation({
  mutationFn: () => bibsHttpService.assignMissing(eventId.value!),
  onSuccess: (result) => {
    notify(true, t('bibs.toasts.assigned', { n: result.assignedCount }))
    invalidatePool()
  },
  onError: onApiError,
})

function onAssign() {
  confirm.require({
    message: t('bibs.confirm.assign'),
    header: t('bibs.actions.assign'),
    icon: 'pi pi-exclamation-triangle',
    rejectProps: { label: t('common.cancel'), severity: 'secondary', outlined: true },
    acceptProps: { label: t('bibs.actions.assign'), severity: 'success' },
    accept: () => assignMutation.mutate(),
  })
}

// ──────────────── Перевыдача (reissue) ────────────────
const reissueVisible = ref(false)
const reissueForce = ref(false)
const reissueSelectedCategories = ref<string[]>([])

const reissueMutation = useMutation({
  mutationFn: () =>
    bibsHttpService.reissue(eventId.value!, {
      categoryIds: reissueSelectedCategories.value.length ? reissueSelectedCategories.value : null,
      force: reissueForce.value,
    }),
  onSuccess: (result) => {
    reissueVisible.value = false
    notify(
      true,
      result.warnings.length
        ? t('bibs.toasts.reissuedWithWarnings', {
            n: result.movedCount,
            w: result.warnings.join('; '),
          })
        : t('bibs.toasts.reissued', { n: result.movedCount }),
    )
    invalidatePool()
  },
  onError: onApiError,
})

// Перевыдача перенумеровывает всех — обязательное подтверждение перед запуском.
function onReissueConfirm() {
  confirm.require({
    message: t('bibs.confirm.reissue'),
    header: t('bibs.reissue.title'),
    icon: 'pi pi-exclamation-triangle',
    rejectProps: { label: t('common.cancel'), severity: 'secondary', outlined: true },
    acceptProps: { label: t('bibs.actions.reissue'), severity: 'warn' },
    accept: () => reissueMutation.mutate(),
  })
}

// ──────────────── Переназначение биба участнику ────────────────
const reassignVisible = ref(false)
const reassignRegistrationId = ref('')
const reassignFullName = ref('')
const reassignCategoryId = ref<string | null>(null)
const reassignNumber = ref<number | null>(null)

function openReassign(registrationId: string, fullName: string, categoryId: string) {
  reassignRegistrationId.value = registrationId
  reassignFullName.value = fullName
  reassignCategoryId.value = categoryId
  reassignNumber.value = null
  reassignVisible.value = true
}

// Свободные номера категории регистрации — для выпадающего списка «Назначить номер»
// (переиспользуем послотовый эндпоинт, фильтруем Free на клиенте).
const reassignSlotsQuery = useQuery({
  queryKey: computed(() => ['admin', 'bibs', 'slots', eventId.value, reassignCategoryId.value]),
  queryFn: () =>
    bibsHttpService.getSlots(eventId.value!, { categoryId: reassignCategoryId.value! }),
  enabled: computed(() => reassignVisible.value && !!eventId.value && !!reassignCategoryId.value),
})
const reassignFreeOptions = computed(() =>
  (reassignSlotsQuery.data.value ?? [])
    .filter((s) => s.statusCode === 'Free')
    .map((s) => ({ label: formatBib(s.number), value: s.number })),
)

const reassignMutation = useMutation({
  mutationFn: () => bibsHttpService.reassign(reassignRegistrationId.value, reassignNumber.value!),
  onSuccess: () => {
    reassignVisible.value = false
    notify(true, t('bibs.toasts.reassigned'))
    invalidatePool()
  },
  onError: onApiError,
})

// ──────────────── Печать ────────────────
const printVisible = ref(false)
const printCategoryId = ref('')
const printMode = ref<PrintMode>('Personalized')
const printSelection = ref<PrintSelection>('assigned')
// Для «Только свободные» — печатать N с запасом, а не все.
const DEFAULT_FREE_LIMIT = 20
const printFreeLimit = ref(DEFAULT_FREE_LIMIT)
// «Свободно» в редактируемой категории — верхняя граница запаса.
const printCategoryFree = computed(
  () => categories.value.find((c) => c.categoryId === printCategoryId.value)?.freeCount ?? 0,
)
// Сколько реально напечатается: не больше, чем свободно.
const printWillPrint = computed(() => Math.min(printFreeLimit.value ?? 0, printCategoryFree.value))

const printModeOptions = computed<{ label: string; value: PrintMode }[]>(() => [
  { label: t('bibs.print.modePersonalized'), value: 'Personalized' },
  { label: t('bibs.print.modePlain'), value: 'Plain' },
])
const printSelectionOptions = computed<{ label: string; value: PrintSelection }[]>(() => [
  { label: t('bibs.print.selectionAssigned'), value: 'assigned' },
  { label: t('bibs.print.selectionAssignedUnprinted'), value: 'assigned_unprinted' },
  { label: t('bibs.print.selectionFree'), value: 'free' },
  { label: t('bibs.print.selectionAll'), value: 'all' },
])
// Именной режим печатает только назначенные (у свободных нет ФИО) → «Только свободные» не предлагаем.
function selectionOptionsFor(mode: PrintMode) {
  return printSelectionOptions.value.filter((o) => mode !== 'Personalized' || o.value !== 'free')
}

function openPrint(categoryId: string) {
  printCategoryId.value = categoryId
  printMode.value = 'Personalized'
  printSelection.value = 'assigned'
  printFreeLimit.value = DEFAULT_FREE_LIMIT
  printVisible.value = true
}

const printMutation = useMutation({
  mutationFn: () =>
    bibsHttpService.print(eventId.value!, {
      categoryId: printCategoryId.value,
      mode: printMode.value,
      selection: printSelection.value,
      freeLimit: printSelection.value === 'free' ? printFreeLimit.value : null,
    }),
  onSuccess: (result) => {
    printVisible.value = false
    notify(true, t('bibs.toasts.printed', { n: result.count }))
    warnFontsIfAny(result.fontWarnings)
    invalidateBatches()
    // Генерация синхронная — файл готов; сразу скачиваем (он же остаётся в «Истории печати»).
    if (eventId.value)
      bibsHttpService.downloadPrintBatchPdf(eventId.value, result.batchId).catch(onApiError)
  },
  onError: (err) => onPrintError(err, printSelection.value, () => (printVisible.value = false)),
})

// ──────────────── Пакетная печать (все категории → общий PDF) ────────────────
const batchPrintVisible = ref(false)
const batchPrintMode = ref<PrintMode>('Personalized')
const batchPrintSelection = ref<PrintSelection>('assigned_unprinted')
const batchPrintFreeLimit = ref(DEFAULT_FREE_LIMIT)

function openBatchPrint() {
  batchPrintMode.value = 'Personalized'
  batchPrintSelection.value = 'assigned_unprinted'
  batchPrintFreeLimit.value = DEFAULT_FREE_LIMIT
  batchPrintVisible.value = true
}

const batchPrintMutation = useMutation({
  mutationFn: () =>
    bibsHttpService.printAll(eventId.value!, {
      mode: batchPrintMode.value,
      selection: batchPrintSelection.value,
      freeLimit: batchPrintSelection.value === 'free' ? batchPrintFreeLimit.value : null,
    }),
  onSuccess: (result) => {
    batchPrintVisible.value = false
    const base = t('bibs.toasts.printedAll', { n: result.count })
    const detail = result.skippedCategoryIds.length
      ? `${base}; ${t('bibs.toasts.batchSkipped', {
          cats: result.skippedCategoryIds.map((id) => labelOf(id)).join(', '),
        })}`
      : base
    notify(true, detail)
    warnFontsIfAny(result.fontWarnings)
    invalidateBatches()
    invalidatePool()
    // Генерация синхронная — файл готов; сразу скачиваем (он же остаётся в «Истории печати»).
    if (eventId.value)
      bibsHttpService.downloadPrintBatchPdf(eventId.value, result.batchId).catch(onApiError)
  },
  onError: (err) =>
    onPrintError(err, batchPrintSelection.value, () => (batchPrintVisible.value = false)),
})

// Именной режим несовместим с «Только свободные» — при переключении на именной сбрасываем выбор.
watch(printMode, (m) => {
  if (m === 'Personalized' && printSelection.value === 'free') printSelection.value = 'assigned'
})
watch(batchPrintMode, (m) => {
  if (m === 'Personalized' && batchPrintSelection.value === 'free')
    batchPrintSelection.value = 'assigned_unprinted'
})

// ──────────────── Вкладка «Шаблоны» ────────────────
const templateEditorVisible = ref(false)
const templateCategoryId = ref('')
const templateCategoryName = ref('')
// Структурные поля категории — для локализованной метки (язык отображения в редакторе).
const templateCategoryParts = ref<CategoryLabelParts | undefined>()

// Категории с шаблоном (кроме редактируемой) — источники для «взять за основу» в редакторе.
const baseCandidates = computed(() =>
  categoriesSorted.value
    .filter((c) => c.hasTemplate && c.categoryId !== templateCategoryId.value)
    .map((c) => ({ id: c.categoryId, label: catLabel(c) })),
)

// Номер-образец для превью редактора — первый номер диапазона редактируемой категории.
const templateSampleNumber = computed(() => {
  const c = categories.value.find((x) => x.categoryId === templateCategoryId.value)
  if (!c || !c.ranges.length) return undefined
  return formatBib(Math.min(...c.ranges.map((r) => r.start)))
})

function openTemplateEditor(category: BibCategoryPool) {
  templateCategoryId.value = category.categoryId
  templateCategoryName.value = catLabel(category)
  templateCategoryParts.value = {
    categoryDistanceKm: category.distanceKm,
    categoryGenderCode: category.genderCode,
    categoryAgeMin: category.ageMin,
    categoryAgeMax: category.ageMax,
    categoryIsGroupRace: category.isGroupRace,
  }
  templateEditorVisible.value = true
}
// Редактор сам сохраняет (без emit) — по закрытию обновляем статус «есть шаблон».
function onTemplateEditorHide() {
  invalidatePool()
}

// ──────────────── Копирование шаблона в другие категории (#1) ────────────────
const copyVisible = ref(false)
const copySourceId = ref('')
const copySourceName = ref('')
const copyTargetIds = ref<string[]>([])
const copyBusy = ref(false)

function openCopyTemplate(category: BibCategoryPool) {
  copySourceId.value = category.categoryId
  copySourceName.value = catLabel(category)
  copyTargetIds.value = []
  copyVisible.value = true
}

// Цели копирования — все категории мероприятия, кроме источника.
const copyTargetOptions = computed(() =>
  categoryOptions.value.filter((o) => o.value !== copySourceId.value),
)

async function doCopyTemplate() {
  if (!copyTargetIds.value.length) return
  copyBusy.value = true
  try {
    const copied = await bibsHttpService.copyTemplate(copySourceId.value, copyTargetIds.value)
    toast.add({
      severity: 'success',
      summary: t('bibs.templates.copyDoneTitle'),
      detail: t('bibs.templates.copyDone', { count: copied }),
      life: 4000,
    })
    copyVisible.value = false
    invalidatePool()
  } catch (e) {
    onApiError(e)
  } finally {
    copyBusy.value = false
  }
}

// ──────────────── Скачивание PDF партии ────────────────
function onDownloadPdf(batchId: string) {
  if (!eventId.value) return
  bibsHttpService.downloadPrintBatchPdf(eventId.value, batchId).catch(onApiError)
}

function formatDateTime(value: string): string {
  const d = new Date(value)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function categoryTotal(cat: BibCategoryPool): number {
  return cat.ranges.reduce((sum, r) => sum + (r.end - r.start + 1), 0)
}

// ──────────────── Вкладка «Пул номеров» (послотовый список) ────────────────
const slotCategoryId = ref<string | null>(null)
const slotStatusFilter = ref<string | null>(null)
const historyCategoryId = ref<string | null>(null)

// Смена мероприятия — сбрасываем фильтры категории (id чужого события не валиден),
// дефолт первой категории нового события проставит watch ниже.
watch(eventId, () => {
  slotCategoryId.value = null
  slotStatusFilter.value = null
  historyCategoryId.value = null
})

// Дефолт фильтра — первая категория по сортировке диапазонов (грузим послотно, не весь пул).
watch(
  categoriesSorted,
  (list) => {
    if (!slotCategoryId.value && list.length) slotCategoryId.value = list[0].categoryId
  },
  { immediate: true },
)

const slotsQuery = useQuery({
  queryKey: computed(() => ['admin', 'bibs', 'slots', eventId.value, slotCategoryId.value]),
  queryFn: () => bibsHttpService.getSlots(eventId.value!, { categoryId: slotCategoryId.value }),
  enabled: computed(() => !!eventId.value && !!slotCategoryId.value),
})

// Опции статуса — из самих слотов (подписи из справочника БД, не из i18n фронта).
const slotStatusOptions = computed(() => {
  const seen = new Map<string, string>()
  for (const s of slotsQuery.data.value ?? []) {
    if (!seen.has(s.statusCode)) seen.set(s.statusCode, s.statusName ?? s.statusCode)
  }
  return [...seen.entries()].map(([value, label]) => ({ label, value }))
})

const slotRows = computed(() => {
  const rows = slotsQuery.data.value ?? []
  return slotStatusFilter.value ? rows.filter((s) => s.statusCode === slotStatusFilter.value) : rows
})

// ──────────────── Вкладка «История выдачи» ────────────────
const historyQuery = useQuery({
  queryKey: computed(() => ['admin', 'bibs', 'history', eventId.value, historyCategoryId.value]),
  queryFn: () =>
    bibsHttpService.getIssuanceHistory(eventId.value!, { categoryId: historyCategoryId.value }),
  enabled: computed(() => !!eventId.value),
})
</script>

<template>
  <section class="flex flex-col gap-4 w-full flex-1 min-h-0">
    <!-- Шапка: заголовок слева, выбор мероприятия справа в одну строку (#7) -->
    <div class="flex flex-wrap items-center justify-between gap-3 w-full">
      <h1 class="text-xl font-bold">{{ t('bibs.title') }}</h1>
      <div class="flex items-center gap-2">
        <EventSelect v-model="eventId" data-test="bibs-event-select" />
      </div>
    </div>

    <template v-if="eventId">
      <Tabs v-model:value="activeTabKey" class="w-full flex-1 min-h-0 bibs-tabs">
        <TabList>
          <Tab value="main">{{ t('bibs.tabs.main') }}</Tab>
          <Tab value="unassigned">{{
            withoutNumber.length
              ? `${t('bibs.tabs.unassigned')} (${withoutNumber.length})`
              : t('bibs.tabs.unassigned')
          }}</Tab>
          <Tab value="pool">{{ t('bibs.tabs.pool') }}</Tab>
          <Tab value="issuanceHistory">{{ t('bibs.tabs.issuanceHistory') }}</Tab>
          <Tab value="printHistory">{{ t('bibs.tabs.printHistory') }}</Tab>
        </TabList>
        <TabPanels>
          <!-- ───────── Вкладка: Основное ───────── -->
          <TabPanel value="main">
            <div v-if="activeTabKey === 'main'" class="flex flex-col gap-4 pt-2 bibs-tab-body">
              <!-- Панель действий -->
              <div class="flex flex-wrap gap-2 bibs-toolbar">
                <Button
                  :label="t('bibs.actions.assign')"
                  icon="pi pi-user-plus"
                  severity="success"
                  outlined
                  :loading="assignMutation.isPending.value"
                  :disabled="poolQuery.isLoading.value"
                  data-test="btn-assign-missing"
                  @click="onAssign"
                />
                <Button
                  :label="t('bibs.actions.reissue')"
                  icon="pi pi-refresh"
                  severity="danger"
                  outlined
                  :disabled="poolQuery.isLoading.value"
                  @click="reissueVisible = true"
                />
                <Button
                  :label="t('bibs.actions.batchPrint')"
                  icon="pi pi-print"
                  severity="info"
                  outlined
                  :disabled="poolQuery.isLoading.value"
                  data-test="btn-batch-print"
                  @click="openBatchPrint"
                />
                <Button
                  :label="t('bibs.actions.editRanges')"
                  icon="pi pi-sliders-h"
                  severity="info"
                  outlined
                  :disabled="poolQuery.isLoading.value"
                  data-test="btn-edit-ranges"
                  @click="rangesVisible = true"
                />
              </div>

              <!-- Пул по категориям -->
              <DataTable
                :value="categoriesSorted"
                :loading="poolQuery.isLoading.value"
                data-key="categoryId"
                data-test="bibs-pool-table"
                scrollable
                scroll-height="flex"
                class="w-full"
              >
                <template #empty>
                  <div class="text-sm text-surface-500 py-4">{{ t('bibs.categoriesEmpty') }}</div>
                </template>

                <Column :header="t('bibs.columns.category')">
                  <template #body="{ data }">
                    <span class="font-medium">{{ catLabel(data) }}</span>
                  </template>
                </Column>
                <Column :header="t('bibs.columns.ranges')">
                  <template #body="{ data }">
                    <div class="flex flex-col gap-1">
                      <span v-for="r in data.ranges" :key="r.id" class="text-sm">
                        {{ formatBib(r.start) }}–{{ formatBib(r.end) }}
                        <span v-if="r.label" class="text-surface-500 ml-1">({{ r.label }})</span>
                      </span>
                      <span v-if="!data.ranges.length" class="text-surface-500 text-sm">—</span>
                    </div>
                  </template>
                </Column>
                <Column :header="t('bibs.columns.total')">
                  <template #body="{ data }">
                    {{ categoryTotal(data) }}
                  </template>
                </Column>
                <Column :header="t('bibs.columns.assigned')">
                  <template #body="{ data }">
                    {{ data.assignedCount }}
                  </template>
                </Column>
                <Column :header="t('bibs.columns.free')">
                  <template #body="{ data }">
                    <span :data-test="`pool-free-${data.categoryId}`">{{ data.freeCount }}</span>
                  </template>
                </Column>
                <Column :header="t('bibs.columns.withoutNumber')">
                  <template #body="{ data }">
                    <span :data-test="`pool-without-number-${data.categoryId}`">
                      {{ withoutNumberCountByCategory.get(data.categoryId) ?? 0 }}
                    </span>
                  </template>
                </Column>
                <Column :header="t('bibs.columns.printed')">
                  <template #body="{ data }">
                    {{ data.printedCount }}
                  </template>
                </Column>
                <Column :header="t('bibs.columns.void')">
                  <template #body="{ data }">
                    {{ data.voidCount }}
                  </template>
                </Column>
                <!-- Шаблон нагрудника: статус + редактирование + копирование (перенесено из вкладки «Шаблоны») -->
                <Column :header="t('bibs.columns.template')">
                  <template #body="{ data }">
                    <div class="flex items-center gap-1">
                      <Tag
                        :value="
                          data.hasTemplate ? t('bibs.templates.has') : t('bibs.templates.none')
                        "
                        :severity="data.hasTemplate ? 'success' : 'secondary'"
                        :data-test="`template-status-${data.categoryId}`"
                      />
                      <Button
                        icon="pi pi-pencil"
                        severity="secondary"
                        text
                        size="small"
                        :title="t('bibs.templates.edit')"
                        :aria-label="t('bibs.templates.edit')"
                        :data-test="`btn-edit-template-${data.categoryId}`"
                        @click="openTemplateEditor(data)"
                      />
                      <Button
                        v-if="data.hasTemplate"
                        icon="pi pi-copy"
                        severity="secondary"
                        text
                        size="small"
                        :title="t('bibs.templates.copyTo')"
                        :aria-label="t('bibs.templates.copyTo')"
                        :data-test="`btn-copy-template-${data.categoryId}`"
                        @click="openCopyTemplate(data)"
                      />
                    </div>
                  </template>
                </Column>
                <Column :header="t('bibs.columns.actions')">
                  <template #body="{ data }">
                    <Button
                      :label="t('bibs.actions.print')"
                      icon="pi pi-print"
                      severity="info"
                      outlined
                      size="small"
                      :data-test="`btn-print-${data.categoryId}`"
                      @click="openPrint(data.categoryId)"
                    />
                  </template>
                </Column>
              </DataTable>
            </div>
          </TabPanel>

          <!-- ───────── Вкладка: Без номера ───────── -->
          <TabPanel value="unassigned">
            <div
              v-if="activeTabKey === 'unassigned'"
              class="flex flex-col gap-4 pt-2 bibs-tab-body"
            >
              <div class="flex flex-wrap gap-2 bibs-toolbar">
                <Button
                  :label="t('bibs.actions.assign')"
                  icon="pi pi-user-plus"
                  severity="success"
                  outlined
                  :loading="assignMutation.isPending.value"
                  :disabled="poolQuery.isLoading.value"
                  @click="onAssign"
                />
              </div>
              <DataTable
                :value="withoutNumber"
                data-key="registrationId"
                scrollable
                scroll-height="flex"
                class="w-full"
              >
                <template #empty>
                  <div class="text-sm text-surface-500 py-4">
                    {{ t('bibs.withoutNumberEmpty') }}
                  </div>
                </template>
                <Column :header="t('bibs.columns.fullName')">
                  <template #body="{ data }">{{ data.fullName }}</template>
                </Column>
                <Column :header="t('bibs.columns.category')">
                  <template #body="{ data }">{{
                    labelOf(data.categoryId, data.categoryName)
                  }}</template>
                </Column>
                <Column :header="t('bibs.columns.actions')">
                  <template #body="{ data }">
                    <Button
                      :label="t('bibs.actions.reassign')"
                      icon="pi pi-pencil"
                      severity="secondary"
                      size="small"
                      @click="openReassign(data.registrationId, data.fullName, data.categoryId)"
                    />
                  </template>
                </Column>
              </DataTable>
            </div>
          </TabPanel>

          <!-- ───────── Вкладка: Пул номеров ───────── -->
          <TabPanel value="pool">
            <div v-if="activeTabKey === 'pool'" class="flex flex-col gap-4 pt-2 bibs-tab-body">
              <div class="flex flex-wrap items-center gap-3 bibs-toolbar">
                <Select
                  v-model="slotCategoryId"
                  :options="categoryOptions"
                  option-label="label"
                  option-value="value"
                  :placeholder="t('bibs.slots.filterCategory')"
                  class="w-72"
                  data-test="slots-category-select"
                />
                <Select
                  v-model="slotStatusFilter"
                  :options="slotStatusOptions"
                  option-label="label"
                  option-value="value"
                  show-clear
                  :placeholder="t('bibs.slots.statusAll')"
                  class="w-56"
                  data-test="slots-status-select"
                />
              </div>

              <DataTable
                :value="slotRows"
                :loading="slotsQuery.isLoading.value"
                data-key="number"
                data-test="bibs-slots-table"
                scrollable
                scroll-height="flex"
                :virtual-scroller-options="{ itemSize: 46 }"
                class="w-full"
              >
                <template #empty>
                  <div class="text-sm text-surface-500 py-4">{{ t('bibs.slots.empty') }}</div>
                </template>
                <Column :header="t('bibs.slots.columns.number')">
                  <template #body="{ data }">
                    <span class="font-medium tabular-nums">{{ formatBib(data.number) }}</span>
                  </template>
                </Column>
                <Column :header="t('bibs.slots.columns.status')">
                  <template #body="{ data }">{{ data.statusName ?? data.statusCode }}</template>
                </Column>
                <Column :header="t('bibs.slots.columns.owner')">
                  <template #body="{ data }">{{ data.fullName ?? '—' }}</template>
                </Column>
                <Column :header="t('bibs.slots.columns.printed')">
                  <template #body="{ data }">
                    {{ data.printed ? t('bibs.slots.printedYes') : t('bibs.slots.printedNo') }}
                  </template>
                </Column>
              </DataTable>
            </div>
          </TabPanel>

          <!-- ───────── Вкладка: История выдачи ───────── -->
          <TabPanel value="issuanceHistory">
            <div
              v-if="activeTabKey === 'issuanceHistory'"
              class="flex flex-col gap-4 pt-2 bibs-tab-body"
            >
              <div class="flex items-center gap-2 bibs-toolbar">
                <Select
                  v-model="historyCategoryId"
                  :options="categoryOptions"
                  option-label="label"
                  option-value="value"
                  show-clear
                  :placeholder="t('bibs.slots.categoryAll')"
                  class="w-72"
                  data-test="history-category-select"
                />
              </div>

              <DataTable
                :value="historyQuery.data.value ?? []"
                :loading="historyQuery.isLoading.value"
                data-key="id"
                data-test="bibs-history-table"
                scrollable
                scroll-height="flex"
                :virtual-scroller-options="{ itemSize: 46 }"
                class="w-full"
              >
                <template #empty>
                  <div class="text-sm text-surface-500 py-4">
                    {{ t('bibs.issuanceHistory.empty') }}
                  </div>
                </template>
                <Column :header="t('bibs.issuanceHistory.columns.changedAt')">
                  <template #body="{ data }">{{ formatDateTime(data.changedAt) }}</template>
                </Column>
                <Column :header="t('bibs.columns.category')">
                  <template #body="{ data }">{{ labelOf(data.categoryId) }}</template>
                </Column>
                <Column :header="t('bibs.issuanceHistory.columns.number')">
                  <template #body="{ data }">
                    <span class="font-medium tabular-nums">{{ formatBib(data.number) }}</span>
                  </template>
                </Column>
                <Column :header="t('bibs.issuanceHistory.columns.change')">
                  <template #body="{ data }">
                    {{ data.fromStatusName ?? '—' }} → {{ data.toStatusName ?? '—' }}
                  </template>
                </Column>
                <Column :header="t('bibs.issuanceHistory.columns.participant')">
                  <template #body="{ data }">
                    <span v-if="data.fromFullName">{{ data.fromFullName }} → </span>
                    {{ data.toFullName ?? '—' }}
                  </template>
                </Column>
                <Column :header="t('bibs.issuanceHistory.columns.changedBy')">
                  <template #body="{ data }">{{ data.changedByName ?? '—' }}</template>
                </Column>
              </DataTable>
            </div>
          </TabPanel>

          <!-- ───────── Вкладка: История печати ───────── -->
          <TabPanel value="printHistory">
            <div
              v-if="activeTabKey === 'printHistory'"
              class="flex flex-col gap-4 pt-2 bibs-tab-body"
            >
              <div class="flex flex-wrap gap-2 bibs-toolbar">
                <Button
                  :label="t('bibs.actions.batchPrint')"
                  icon="pi pi-print"
                  severity="info"
                  outlined
                  :disabled="poolQuery.isLoading.value"
                  data-test="btn-batch-print-history"
                  @click="openBatchPrint"
                />
              </div>
              <DataTable
                :value="printBatches"
                :loading="batchesQuery.isLoading.value"
                data-key="id"
                data-test="print-batches-table"
                scrollable
                scroll-height="flex"
                class="w-full"
              >
                <template #empty>
                  <div class="text-sm text-surface-500 py-4">
                    {{ t('bibs.printBatches.empty') }}
                  </div>
                </template>
                <Column :header="t('bibs.printBatches.columns.category')">
                  <template #body="{ data }">
                    {{
                      data.categoryId
                        ? labelOf(data.categoryId)
                        : t('bibs.printBatches.allCategories')
                    }}
                  </template>
                </Column>
                <Column :header="t('bibs.printBatches.columns.mode')">
                  <template #body="{ data }">{{ data.modeName }}</template>
                </Column>
                <Column :header="t('bibs.printBatches.columns.count')">
                  <template #body="{ data }">
                    <span data-test="batch-count">{{ data.count }}</span>
                  </template>
                </Column>
                <Column :header="t('bibs.printBatches.columns.createdAt')">
                  <template #body="{ data }">{{ formatDateTime(data.createdAt) }}</template>
                </Column>
                <Column :header="t('bibs.printBatches.columns.actions')">
                  <template #body="{ data }">
                    <Button
                      :label="t('bibs.printBatches.actions.downloadPdf')"
                      icon="pi pi-file-pdf"
                      class="btn-pdf"
                      outlined
                      size="small"
                      @click="onDownloadPdf(data.id)"
                    />
                  </template>
                </Column>
              </DataTable>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </template>

    <div v-else class="text-sm text-surface-500">{{ t('bibs.selectEvent') }}</div>

    <!-- Диалог перевыдачи -->
    <Dialog
      v-model:visible="reissueVisible"
      :header="t('bibs.reissue.title')"
      :style="{ width: '480px' }"
      modal
    >
      <div class="flex flex-col gap-4">
        <p class="text-sm text-surface-500 m-0">{{ t('bibs.reissue.hint') }}</p>
        <div>
          <label class="block text-sm font-medium mb-1">{{ t('bibs.reissue.categories') }}</label>
          <MultiSelect
            v-model="reissueSelectedCategories"
            :options="categoryOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('bibs.reissue.allCategories')"
            class="w-full"
          />
        </div>
        <div class="flex items-center gap-2">
          <Checkbox v-model="reissueForce" :binary="true" input-id="reissue-force" />
          <label for="reissue-force" class="text-sm">{{ t('bibs.reissue.force') }}</label>
        </div>
      </div>
      <template #footer>
        <Button
          :label="t('common.cancel')"
          severity="secondary"
          outlined
          @click="reissueVisible = false"
        />
        <Button
          :label="t('bibs.actions.reissue')"
          icon="pi pi-refresh"
          :loading="reissueMutation.isPending.value"
          @click="onReissueConfirm"
        />
      </template>
    </Dialog>

    <!-- Диалог переназначения номера -->
    <Dialog
      v-model:visible="reassignVisible"
      :header="t('bibs.reassign.title')"
      :style="{ width: '400px' }"
      modal
    >
      <div class="flex flex-col gap-4">
        <p class="text-sm m-0">{{ reassignFullName }}</p>
        <div>
          <label class="block text-sm font-medium mb-1">{{ t('bibs.reassign.number') }}</label>
          <!-- Выпадающий список свободных номеров пула категории (с поиском). -->
          <Select
            v-model="reassignNumber"
            :options="reassignFreeOptions"
            option-label="label"
            option-value="value"
            filter
            :loading="reassignSlotsQuery.isLoading.value"
            :placeholder="t('bibs.reassign.pickNumber')"
            class="w-full"
          >
            <template #empty>
              <span class="text-sm text-surface-500">{{ t('bibs.reassign.noFree') }}</span>
            </template>
          </Select>
        </div>
      </div>
      <template #footer>
        <Button
          :label="t('common.cancel')"
          severity="secondary"
          outlined
          @click="reassignVisible = false"
        />
        <Button
          :label="t('bibs.actions.reassign')"
          icon="pi pi-check"
          :disabled="!reassignNumber"
          :loading="reassignMutation.isPending.value"
          @click="reassignMutation.mutate()"
        />
      </template>
    </Dialog>

    <!-- Диалог печати -->
    <Dialog
      v-model:visible="printVisible"
      :header="t('bibs.print.title')"
      :style="{ width: '440px' }"
      modal
    >
      <div class="flex flex-col gap-4">
        <div>
          <label class="block text-sm font-medium mb-1">{{ t('bibs.print.mode') }}</label>
          <Select
            v-model="printMode"
            :options="printModeOptions"
            option-label="label"
            option-value="value"
            class="w-full"
          />
        </div>
        <div>
          <label class="block text-sm font-medium mb-1">{{ t('bibs.print.selection') }}</label>
          <Select
            v-model="printSelection"
            :options="selectionOptionsFor(printMode)"
            option-label="label"
            option-value="value"
            class="w-full"
          />
        </div>
        <div v-if="printSelection === 'free'">
          <label class="block text-sm font-medium mb-1">{{ t('bibs.print.freeLimit') }}</label>
          <InputNumber
            v-model="printFreeLimit"
            :min="1"
            :max="1000"
            :use-grouping="false"
            show-buttons
            class="w-full"
          />
          <Message severity="warn" :closable="false" class="mt-2 text-xs">
            <span class="whitespace-pre-line">{{
              t('bibs.print.freeLimitInfo', {
                N: printFreeLimit ?? 0,
                x: printCategoryFree,
                y: printWillPrint,
              })
            }}</span>
          </Message>
        </div>
        <p class="text-xs text-surface-500 m-0">{{ t('bibs.print.fileUrlNote') }}</p>
      </div>
      <template #footer>
        <Button
          :label="t('common.cancel')"
          severity="secondary"
          outlined
          @click="printVisible = false"
        />
        <Button
          :label="t('bibs.actions.print')"
          icon="pi pi-print"
          :loading="printMutation.isPending.value"
          data-test="btn-print-confirm"
          @click="printMutation.mutate()"
        />
      </template>
    </Dialog>

    <!-- Диалог пакетной печати (все категории → общий PDF) -->
    <Dialog
      v-model:visible="batchPrintVisible"
      :header="t('bibs.batchPrint.title')"
      :style="{ width: '460px' }"
      modal
    >
      <div class="flex flex-col gap-4">
        <p class="text-sm text-surface-500 m-0">{{ t('bibs.batchPrint.hint') }}</p>
        <div>
          <label class="block text-sm font-medium mb-1">{{ t('bibs.print.mode') }}</label>
          <Select
            v-model="batchPrintMode"
            :options="printModeOptions"
            option-label="label"
            option-value="value"
            class="w-full"
          />
        </div>
        <div>
          <label class="block text-sm font-medium mb-1">{{ t('bibs.print.selection') }}</label>
          <Select
            v-model="batchPrintSelection"
            :options="selectionOptionsFor(batchPrintMode)"
            option-label="label"
            option-value="value"
            class="w-full"
          />
        </div>
        <div v-if="batchPrintSelection === 'free'">
          <label class="block text-sm font-medium mb-1">{{ t('bibs.print.freeLimit') }}</label>
          <InputNumber
            v-model="batchPrintFreeLimit"
            :min="1"
            :max="1000"
            :use-grouping="false"
            show-buttons
            class="w-full"
          />
          <Message severity="warn" :closable="false" class="mt-2 text-xs">
            <span class="whitespace-pre-line">{{
              t('bibs.print.freeLimitBatchInfo', { N: batchPrintFreeLimit ?? 0 })
            }}</span>
          </Message>
        </div>
      </div>
      <template #footer>
        <Button
          :label="t('common.cancel')"
          severity="secondary"
          outlined
          @click="batchPrintVisible = false"
        />
        <Button
          :label="t('bibs.actions.batchPrint')"
          icon="pi pi-print"
          :loading="batchPrintMutation.isPending.value"
          data-test="btn-batch-print-confirm"
          @click="batchPrintMutation.mutate()"
        />
      </template>
    </Dialog>

    <!-- Диалог редактора шаблона категории. Фиксированная высота — окно не скачет при
         загрузке фона/разворачивании блоков; содержимое скроллится внутри колонок. -->
    <Dialog
      v-model:visible="templateEditorVisible"
      :header="`${t('bibTemplate.title')} — ${templateCategoryName}`"
      :style="{ width: '95vw', maxWidth: '1176px', height: '90vh' }"
      class="bib-editor-dialog"
      modal
      @hide="onTemplateEditorHide"
    >
      <BibTemplateEditor
        v-if="templateEditorVisible"
        :category-id="templateCategoryId"
        :category-name="templateCategoryName"
        :category-parts="templateCategoryParts"
        :base-candidates="baseCandidates"
        :sample-number="templateSampleNumber"
        @close="templateEditorVisible = false"
      />
    </Dialog>

    <!-- Диапазоны номеров — пакетно по всем категориям (перенесено из «Мероприятий») -->
    <Dialog
      v-model:visible="rangesVisible"
      :header="t('bibs.actions.editRanges')"
      :style="{ width: '95vw', maxWidth: '720px', height: '85vh' }"
      class="bib-editor-dialog"
      modal
    >
      <BibRangesEditor
        v-if="rangesVisible && eventId"
        :event-id="eventId"
        :categories="rangesEditorCategories"
        @saved="onRangesSaved"
      />
    </Dialog>

    <!-- Копирование шаблона в другие категории (#1) -->
    <Dialog
      v-model:visible="copyVisible"
      :header="t('bibs.templates.copyTo')"
      :style="{ width: '480px' }"
      modal
    >
      <div class="flex flex-col gap-4">
        <p class="text-sm text-surface-500 m-0">
          {{ t('bibs.templates.copyHint', { source: copySourceName }) }}
        </p>
        <div>
          <label class="block text-sm font-medium mb-1">{{
            t('bibs.templates.copyTargets')
          }}</label>
          <MultiSelect
            v-model="copyTargetIds"
            :options="copyTargetOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('bibs.templates.copyTargetsPlaceholder')"
            filter
            class="w-full"
          />
        </div>
      </div>
      <template #footer>
        <Button
          :label="t('common.cancel')"
          severity="secondary"
          outlined
          @click="copyVisible = false"
        />
        <Button
          :label="t('bibs.templates.copyAction')"
          icon="pi pi-copy"
          :disabled="!copyTargetIds.length"
          :loading="copyBusy"
          @click="doCopyTemplate"
        />
      </template>
    </Dialog>
  </section>
</template>

<style scoped>
/* Кнопка «Скачать PDF» — тем же PDF-красным, что «Выгрузить в PDF» в регистрациях. */
.btn-pdf {
  background: transparent !important;
  border-color: #fa0f00 !important;
  color: #fa0f00 !important;
}
.btn-pdf:hover {
  background: rgba(250, 15, 0, 0.08) !important;
  border-color: #fa0f00 !important;
}
/* Вкладки растянуты на всю высоту страницы; содержимое каждой вкладки скроллится
   внутри, а шапка вкладок остаётся на месте. Низ — с отступом ~10px. */
.bibs-tabs {
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.bibs-tabs :deep(.p-tabpanels) {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding-bottom: 10px;
  padding-left: 0;
  padding-right: 0;
}
/* Активная панель заполняет высоту (скрытые PrimeVue гасит inline display:none). */
.bibs-tabs :deep(.p-tabpanel) {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
/* Внутри вкладки: тулбар (фильтры/кнопки) закреплён сверху, реестр — со своей прокруткой. */
.bibs-tab-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.bibs-tab-body > .bibs-toolbar {
  flex-shrink: 0;
}
.bibs-tab-body > :deep(.p-datatable) {
  flex: 1;
  min-height: 0;
}
/* Тело диалога редактора занимает всю фиксированную высоту, а скролл — внутри колонок редактора. */
.bib-editor-dialog :deep(.p-dialog-content) {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
/* Колонки не сжимаются под текст: заголовки и ячейки в одну строку (min-ширина =
   ширина содержимого). Если сумма шире области — таблица скроллится по горизонтали. */
.bibs-tabs :deep(.p-datatable-table-container) {
  overflow-x: auto;
}
.bibs-tabs :deep(.p-datatable) th,
.bibs-tabs :deep(.p-datatable) td {
  white-space: nowrap;
}
</style>
